// Water Section component for UtilitySelector

import type { WaterPlan, SelectedService } from '@/types'
import type { ApartmentWaterChoice } from '@/stores/orderStore'

interface WaterSectionProps {
  waterPlans: WaterPlan[]
  selectedWater?: SelectedService
  isExpanded: boolean
  isApartment: boolean
  apartmentWaterChoice: ApartmentWaterChoice
  onToggle: () => void
  onSelectPlan: (plan: WaterPlan) => void
  onRemove?: () => void
  onApartmentChoiceChange: (choice: 'property-managed' | 'self-managed') => void
  waterUnavailable?: boolean
}

// Warning icon SVG component
function WarningIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 9V13M12 17H12.01M4.93 19H19.07C20.4 19 21.27 17.54 20.6 16.4L13.53 4.2C12.86 3.06 11.14 3.06 10.47 4.2L3.4 16.4C2.73 17.54 3.6 19 4.93 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export function WaterSection({
  waterPlans,
  selectedWater,
  isExpanded,
  isApartment,
  apartmentWaterChoice,
  onToggle,
  onSelectPlan,
  onRemove,
  onApartmentChoiceChange,
  waterUnavailable = false,
}: WaterSectionProps) {
  // Determine badge state
  const getBadgeState = () => {
    if (waterUnavailable) {
      return { text: 'UNAVAILABLE', className: 'utility-selector__badge--unavailable' }
    }
    if (isApartment && apartmentWaterChoice === 'property-managed') {
      return { text: 'PROPERTY', className: 'utility-selector__badge--property' }
    }
    if (selectedWater) {
      return { text: 'ADDED', className: 'utility-selector__badge--added' }
    }
    return { text: 'NONE', className: 'utility-selector__badge--none' }
  }

  const badge = getBadgeState()

  // Determine description text
  const getDescription = () => {
    if (waterUnavailable) {
      return 'Water service is not available in your area'
    }
    if (isApartment && apartmentWaterChoice === 'property-managed') {
      return 'Water is managed by your property'
    }
    if (selectedWater) {
      return 'Water plan automatically added to your cart'
    }
    return 'Select a water plan'
  }

  // Should we show plan selection? Only if not apartment OR if apartment and self-managed
  const showPlanSelection = !isApartment || apartmentWaterChoice === 'self-managed'

  return (
    <div
      className={`utility-selector ${selectedWater ? 'utility-selector--selected' : ''} ${isExpanded ? 'utility-selector--expanded' : ''}`}
      data-utility="water"
    >
      <div className="utility-selector__header" onClick={onToggle}>
        <div className="utility-selector__title-group">
          <div className="utility-selector__title">WATER SERVICE</div>
          <div className="utility-selector__description" id="water-service-description">
            {getDescription()}
          </div>
        </div>
        <div className={`utility-selector__badge ${badge.className}`}>
          {badge.text}
        </div>
      </div>
      <div className="utility-selector__content">
        {/* Water unavailable message */}
        {waterUnavailable && (
          <div className="water-unavailable-message">
            <WarningIcon />
            <div className="water-unavailable-message__content">
              <div className="water-unavailable-message__title">
                Water service is not available through our platform for your area.
              </div>
              <p className="water-unavailable-message__body">
                To set up water service, please contact your local city water utility directly.
                They will be able to assist you with establishing service at your new address.
              </p>
              <p className="water-unavailable-message__apology">
                We apologize for the inconvenience. We can still help you set up your other utilities below.
              </p>
            </div>
          </div>
        )}

        {/* Apartment water options */}
        {!waterUnavailable && isApartment && (
          <div className="apartment-water-options">
            <div className="apartment-water-warning">
              <WarningIcon />
              <p>Water service for apartments may be managed by your property. Please select how water is handled.</p>
            </div>
            <div className="apartment-water-choice">
              <label className="apartment-water-option">
                <input
                  type="radio"
                  name="apartment-water"
                  value="property-managed"
                  checked={apartmentWaterChoice === 'property-managed'}
                  onChange={() => onApartmentChoiceChange('property-managed')}
                />
                <div className="apartment-water-option__content">
                  <div className="apartment-water-option__title">Water is managed by my property</div>
                  <div className="apartment-water-option__description">My lease or HOA includes water service</div>
                </div>
              </label>
              <label className="apartment-water-option">
                <input
                  type="radio"
                  name="apartment-water"
                  value="self-managed"
                  checked={apartmentWaterChoice === 'self-managed'}
                  onChange={() => onApartmentChoiceChange('self-managed')}
                />
                <div className="apartment-water-option__content">
                  <div className="apartment-water-option__title">I need to set up water myself</div>
                  <div className="apartment-water-option__description">I am responsible for setting up water service</div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Plan selection - only show if NOT property-managed and water is available */}
        {!waterUnavailable && showPlanSelection && (
          <div className={isApartment ? 'apartment-water-plans' : ''}>
            {/* Single water plan display */}
            {waterPlans.length === 1 ? (
              <div className="water-plan-details" id="water-plan-details">
                <div className="plan-info">
                  <div className="plan-info__logo" id="water-plan-logo">
                    {waterPlans[0].logo && (
                      <img src={waterPlans[0].logo} alt={waterPlans[0].vendorName} />
                    )}
                  </div>
                  <div className="plan-info__details">
                    <div className="plan-info__provider" id="water-plan-provider">
                      {waterPlans[0].vendorName}
                    </div>
                    <div className="plan-info__description" id="water-plan-short-description">
                      {waterPlans[0].shortDescription || waterPlans[0].name}
                    </div>
                  </div>
                </div>
                {selectedWater && onRemove && (
                  <button
                    className="plan-remove-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove()
                    }}
                    type="button"
                  >
                    Remove
                  </button>
                )}
                {!selectedWater && (
                  <button
                    className="plan-add-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      onSelectPlan(waterPlans[0])
                    }}
                    type="button"
                  >
                    Add Water
                  </button>
                )}
              </div>
            ) : (
              /* Multiple water plans grid */
              <div className="plan-grid" id="water-plan-grid">
                {waterPlans.map(plan => {
                  const planId = plan.id || plan.ID
                  const isSelected = selectedWater?.planId === planId
                  return (
                    <div
                      key={planId}
                      className={`plan-card${isSelected ? ' plan-card--selected' : ''}`}
                    >
                      <div className="plan-card__header">
                        <div className="plan-card__title">{plan.name}</div>
                      </div>
                      <div className="plan-card__logo">
                        {plan.logo && <img src={plan.logo} alt={plan.vendorName} />}
                      </div>
                      <div className="plan-card__body">
                        <div className="plan-card__row">
                          <div className="plan-card__label">Provider</div>
                          <div className="plan-card__value">{plan.vendorName}</div>
                        </div>
                        {plan.price && (
                          <div className="plan-card__row">
                            <div className="plan-card__label">Price*</div>
                            <div className="plan-card__value">${plan.price.toFixed(2)} /mo.</div>
                          </div>
                        )}
                      </div>
                      <div className="plan-card__footer">
                        <button
                          className="plan-card__button js-select-utility"
                          onClick={() => onSelectPlan(plan)}
                        >
                          {isSelected ? 'SELECTED' : 'SELECT PLAN'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
